import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { bootstrapStorefront, getActiveStoreContext, parseSlugFromPath } from '../lib/storefront/storeBootstrap';
import { setStorefrontStoreId } from '../Api/api';
import StorePathRedirect from '../Component/storefront/StorePathRedirect';
import KineticLoader from '../Component/UI/KineticLoader/KineticLoader';

const StoreContext = createContext(null);

function applyAxiosStoreHeader(storeId) {
    if (storeId) {
        axios.defaults.headers.common['X-Store-Id'] = storeId;
    } else {
        delete axios.defaults.headers.common['X-Store-Id'];
    }
}

export function StoreProvider({ children }) {
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();
    const [context, setContext] = useState(() => getActiveStoreContext());
    const [loading, setLoading] = useState(!context);
    const [error, setError] = useState(null);
    const storeIdentity = useMemo(() => {
        const slugs = parseSlugFromPath(pathname);
        return slugs ? `${slugs.orgSlug}/${slugs.storeSlug}` : '';
    }, [pathname]);
    const locationRef = useRef({ pathname, search, hash });
    locationRef.current = { pathname, search, hash };

    useEffect(() => {
        let cancelled = false;
        let navigatingAway = false;

        (async () => {
            const location = locationRef.current;
            const hadContext = Boolean(getActiveStoreContext()?.store?.id);
            try {
                if (!hadContext) setLoading(true);
                const resolved = await bootstrapStorefront({ pathname: location.pathname });
                if (cancelled) return;
                setContext(resolved);
                setStorefrontStoreId(resolved.store.id);
                applyAxiosStoreHeader(resolved.store.id);
                setError(null);
            } catch (err) {
                if (cancelled) return;

                if (err.code === 'STORE_TYPE_MISMATCH' && err.peerHref) {
                    navigatingAway = true;
                    setLoading(true);
                    window.location.replace(`${err.peerHref}${location.search}${location.hash}`);
                    return;
                }

                if (err.code === 'STORE_TYPE_MISMATCH_REDIRECT' && err.redirectPath) {
                    navigate(`${err.redirectPath}${location.search}${location.hash}`, { replace: true });
                    return;
                }

                setError(err);
                setContext(null);
                setStorefrontStoreId(null);
                applyAxiosStoreHeader(null);
            } finally {
                if (!cancelled && !navigatingAway) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [storeIdentity, navigate]);

    const value = useMemo(
        () => ({
            store: context?.store || null,
            organization: context?.organization || null,
            loading,
            error,
            ready: Boolean(context?.store?.id) && !loading,
        }),
        [context, loading, error],
    );

    if (loading && !context?.store?.id) {
        return (
            <>
                <StorePathRedirect />
                <div role="status" aria-label="Loading store…">
                    <KineticLoader />
                </div>
            </>
        );
    }

    if (error || !context?.store?.id) {
        const isDev = process.env.NODE_ENV !== 'production';
        const envOrg = String(process.env.REACT_APP_ORG_SLUG || '').trim();
        const envStore = String(process.env.REACT_APP_STORE_SLUG || '').trim();
        const suggestedUrl = envOrg && envStore ? `/s/${envOrg}/${envStore}` : '';
        const message = String(error?.message || '');
        const missingStore = /not configured|not found for this domain/i.test(message) && !storeIdentity;
        const typeMismatch = error?.code === 'STORE_TYPE_MISMATCH' || /type mismatch/i.test(message);
        const peerHref = error?.peerHref || '';

        return (
            <>
                <StorePathRedirect />
                <div style={{ padding: '2rem', maxWidth: '40rem', margin: '0 auto' }}>
                    <h1>
                        {typeMismatch
                            ? 'Wrong storefront app'
                            : missingStore
                              ? 'Store not selected'
                              : 'Store unavailable'}
                    </h1>
                    {typeMismatch ? (
                        <>
                            <p>
                                This store is type <code>{error.actualType || 'unknown'}</code>, but
                                this app only serves <code>{error.expectedType || 'a different type'}</code>
                                .
                            </p>
                            {peerHref ? (
                                <p style={{ marginTop: '1rem' }}>
                                    Open it on the correct storefront:{' '}
                                    <a href={peerHref}>
                                        <code>{peerHref}</code>
                                    </a>
                                </p>
                            ) : (
                                <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.85 }}>
                                    Ask your platform admin for the correct brand URL for{' '}
                                    <code>{error?.actualType || 'this store type'}</code>
                                    {error?.storefrontApp ? (
                                        <>
                                            {' '}
                                            (app: <code>{error.storefrontApp}</code>)
                                        </>
                                    ) : null}
                                    .
                                </p>
                            )}
                        </>
                    ) : missingStore ? (
                        <p>
                            Open a shop using its storefront link. The plain app URL does not load a
                            specific store until a custom domain is connected.
                        </p>
                    ) : (
                        <p>{message || 'Unable to load this storefront.'}</p>
                    )}
                    {isDev && missingStore ? (
                        <>
                            {suggestedUrl ? (
                                <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.85 }}>
                                    Dev: open{' '}
                                    <a href={suggestedUrl}>
                                        <code>{suggestedUrl}</code>
                                    </a>
                                    {' '}or{' '}
                                    <a href={`http://${envStore}.localhost:3010`}>
                                        <code>{`http://${envStore}.localhost:3010`}</code>
                                    </a>
                                    .
                                </p>
                            ) : null}
                            <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', opacity: 0.75 }}>
                                Or set <code>REACT_APP_ORG_SLUG</code> + <code>REACT_APP_STORE_SLUG</code>{' '}
                                in <code>.env.development</code> and restart <code>npm start</code>.
                            </p>
                        </>
                    ) : null}
                </div>
            </>
        );
    }

    return (
        <StoreContext.Provider value={value}>
            <StorePathRedirect />
            {children}
        </StoreContext.Provider>
    );
}

export function useStorefront() {
    const ctx = useContext(StoreContext);
    if (!ctx) {
        throw new Error('useStorefront must be used within StoreProvider');
    }
    return ctx;
}
